// Live host telemetry for one Outpost (doc 02 §2, doc 03 §3.2).
//
// The question this answers is "what is this box doing right now, and is
// anything about it wrong" — before a stream fails, not after. Everything here
// comes from the last check-in, so it is at most one poll interval old.
//
// Two rules run through the layout. Nothing is encoded by colour alone: every
// meter carries its number, every state carries its word. And a reading that
// was not collected renders as absent rather than as zero — a chassis with no
// BMC has no fan speeds, and showing "0 RPM" for it would be a fault report.
import { Badge, Card, Empty, Meter, StatusDot, formatAgo } from "../ui";
import type { Tone } from "../ui";

// --- the shape the agent sends ----------------------------------------------
export type Telemetry = {
  kernel?: string;
  uptime_s?: number;
  load?: number[];
  processes?: number;
  cpu?: {
    pct?: number;
    per_core?: number[];
    freq_mhz?: number;
    max_mhz?: number;
    iowait_pct?: number;
    steal_pct?: number;
    throttle_events?: number;
  };
  memory?: {
    total_mb?: number;
    used_mb?: number;
    available_mb?: number;
    cached_mb?: number;
    swap_total_mb?: number;
    swap_used_mb?: number;
    pct?: number;
  };
  thermal?: { name: string; temp_c: number; high_c?: number; crit_c?: number }[];
  fans?: { name: string; rpm: number }[];
  power?: { name: string; watts: number }[];
  filesystems?: {
    mount: string;
    device?: string;
    total_gb: number;
    used_gb: number;
    pct: number;
    inode_pct?: number;
  }[];
  disks?: { dev: string; read_mbps: number; write_mbps: number; util_pct: number; temp_c?: number }[];
  network?: {
    name: string;
    carrier: boolean;
    speed_mbps?: number;
    duplex?: string;
    rx_mbps: number;
    tx_mbps: number;
    rx_errors: number;
    tx_errors: number;
    rx_dropped: number;
    tx_dropped: number;
  }[];
  ecc?: { corrected: number; uncorrected: number };
  time?: { synced: boolean; offset_ms: number };
};

// --- thresholds --------------------------------------------------------------
// Named here rather than inlined so the whole policy is one thing to read and
// argue with. A transcoding box runs hot and busy by design: 70% CPU is the job
// working, not a problem, so the warn line sits where headroom actually starts
// to disappear.
const CPU_WARN = 85;
const CPU_CRIT = 95;
const MEM_WARN = 85;
const MEM_CRIT = 93;
const DISK_WARN = 80;
const DISK_CRIT = 90;
const TEMP_WARN = 75;
const TEMP_CRIT = 88;
const CLOCK_DRIFT_WARN_MS = 250;

function loadTone(pct: number, warn: number, crit: number): Tone {
  if (pct >= crit) return "crit";
  if (pct >= warn) return "warn";
  return "ok";
}

function tempTone(sensor: { temp_c: number; high_c?: number; crit_c?: number }): Tone {
  // Prefer the limits the hardware itself reports — a CPU package and a chassis
  // inlet have very different ideas of hot, and guessing one number for both
  // either cries wolf or misses a genuinely cooking part.
  const crit = sensor.crit_c || TEMP_CRIT;
  const warn = sensor.high_c || TEMP_WARN;
  if (sensor.temp_c >= crit) return "crit";
  if (sensor.temp_c >= warn) return "warn";
  return "ok";
}

const toneBar: Record<Tone, string> = {
  ok: "bg-state-ok",
  warn: "bg-state-warn",
  crit: "bg-state-crit",
  info: "bg-state-info",
  idle: "bg-state-idle",
  brand: "bg-brand",
};

// --- small pieces ------------------------------------------------------------
/** Per-core utilisation. Forty cores is too many for forty labels, so this is
 *  a shape to read at a glance — an even block means balanced work, one tall
 *  bar among flat ones means a pinned thread. */
function CoreBars({ cores }: { cores: number[] }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-ink-400">Per core</span>
        <span className="font-mono text-xs text-ink-400">{cores.length} threads</span>
      </div>
      <div
        className="mt-1.5 flex h-8 items-end gap-px"
        role="img"
        aria-label={`Per-core utilisation across ${cores.length} threads, busiest ${Math.max(
          ...cores,
        ).toFixed(0)} percent`}
      >
        {cores.map((pct, index) => (
          <div
            key={index}
            className={`flex-1 rounded-sm ${toneBar[loadTone(pct, CPU_WARN, CPU_CRIT)]}`}
            style={{ height: `${Math.max(4, Math.min(100, pct))}%` }}
            title={`core ${index}: ${pct.toFixed(0)}%`}
          />
        ))}
      </div>
    </div>
  );
}

function Reading({ label, value, tone }: { label: string; value: string; tone?: Tone }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-400">{label}</div>
      <div
        className={`mt-0.5 font-mono text-sm ${
          tone === "crit"
            ? "text-state-crit"
            : tone === "warn"
              ? "text-state-warn"
              : "text-ink-100"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function gb(mb?: number): string {
  if (!mb) return "—";
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

// --- faults ------------------------------------------------------------------
/** Things that are wrong but that nothing else on the box will report.
 *
 *  Correctable ECC errors are the reason this section exists: nothing fails,
 *  output stays correct, and the count climbs until a bit flips somewhere that
 *  matters. Same for thermal throttling, which shows up to a viewer as dropped
 *  frames and to an operator as nothing at all. */
export function faultsOf(t: Telemetry): { tone: Tone; text: string }[] {
  const faults: { tone: Tone; text: string }[] = [];

  if (t.ecc?.uncorrected) {
    faults.push({
      tone: "crit",
      text: `${t.ecc.uncorrected} uncorrectable ECC error${t.ecc.uncorrected === 1 ? "" : "s"} — this DIMM is failing`,
    });
  }
  if (t.ecc?.corrected) {
    faults.push({
      tone: "warn",
      text: `${t.ecc.corrected} corrected ECC error${t.ecc.corrected === 1 ? "" : "s"} since boot`,
    });
  }
  if (t.cpu?.throttle_events) {
    faults.push({
      tone: "warn",
      text: `CPU thermally throttled ${t.cpu.throttle_events} time${t.cpu.throttle_events === 1 ? "" : "s"} since boot`,
    });
  }
  if (t.time && !t.time.synced) {
    faults.push({ tone: "warn", text: "clock is not synchronised — media tokens expire against wall time" });
  } else if (t.time && Math.abs(t.time.offset_ms) > CLOCK_DRIFT_WARN_MS) {
    faults.push({ tone: "warn", text: `clock offset ${t.time.offset_ms.toFixed(0)} ms` });
  }
  if (t.memory?.swap_used_mb && t.memory.swap_used_mb > 256) {
    faults.push({
      tone: "warn",
      text: `${gb(t.memory.swap_used_mb)} swapped — a transcoder that swaps drops frames`,
    });
  }
  for (const nic of t.network ?? []) {
    const errors = nic.rx_errors + nic.tx_errors;
    if (errors > 0) faults.push({ tone: "warn", text: `${nic.name}: ${errors} interface error${errors === 1 ? "" : "s"}` });
  }
  for (const fs of t.filesystems ?? []) {
    if (fs.pct >= DISK_CRIT) faults.push({ tone: "crit", text: `${fs.mount} is ${fs.pct.toFixed(0)}% full` });
    if ((fs.inode_pct ?? 0) >= DISK_CRIT) {
      faults.push({ tone: "crit", text: `${fs.mount} has ${fs.inode_pct?.toFixed(0)}% of inodes used` });
    }
  }
  return faults;
}

// --- the panel ---------------------------------------------------------------
export default function EndpointVitals({
  telemetry,
  checkinAgo,
}: {
  telemetry: Telemetry | undefined;
  checkinAgo: number | null | undefined;
}) {
  if (!telemetry) {
    return (
      <Card title="Host vitals">
        <Empty>
          No telemetry yet. An agent reports this from its second check-in — the first
          establishes the counters that rates are measured against.
        </Empty>
      </Card>
    );
  }

  const t = telemetry;
  const faults = faultsOf(t);
  const hottest = [...(t.thermal ?? [])].sort((a, b) => b.temp_c - a.temp_c)[0];
  const totalWatts = (t.power ?? []).reduce((sum, rail) => sum + rail.watts, 0);
  const memPct = t.memory?.pct ?? 0;

  return (
    <div className="space-y-6">
      {faults.length > 0 && (
        <Card title="Needs attention">
          <ul className="space-y-2">
            {faults.map((fault, index) => (
              <li key={index} className="text-sm">
                <StatusDot tone={fault.tone} label={fault.text} />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Load"
        actions={<span className="text-xs text-ink-400">as of {formatAgo(checkinAgo)}</span>}
      >
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <Meter
              label="CPU"
              pct={t.cpu?.pct ?? 0}
              tone={loadTone(t.cpu?.pct ?? 0, CPU_WARN, CPU_CRIT)}
              detail={[
                t.cpu?.freq_mhz ? `${(t.cpu.freq_mhz / 1000).toFixed(2)} GHz` : null,
                t.cpu?.iowait_pct ? `${t.cpu.iowait_pct}% iowait` : null,
                t.cpu?.steal_pct ? `${t.cpu.steal_pct}% steal` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
            <Meter
              label="Memory"
              pct={memPct}
              tone={loadTone(memPct, MEM_WARN, MEM_CRIT)}
              detail={`${gb(t.memory?.used_mb)} of ${gb(t.memory?.total_mb)} · ${gb(
                t.memory?.cached_mb,
              )} cached`}
            />
          </div>
          <div className="space-y-4">
            {t.cpu?.per_core?.length ? <CoreBars cores={t.cpu.per_core} /> : null}
            <div className="grid grid-cols-3 gap-4">
              <Reading
                label="Load"
                value={t.load?.length ? t.load.map((v) => v.toFixed(2)).join("  ") : "—"}
              />
              <Reading label="Processes" value={t.processes ? String(t.processes) : "—"} />
              <Reading label="Swap" value={t.memory?.swap_used_mb ? gb(t.memory.swap_used_mb) : "none"} />
            </div>
          </div>
        </div>
      </Card>

      {(t.thermal?.length || t.fans?.length || t.power?.length) && (
        <Card
          title="Thermal and power"
          actions={
            totalWatts > 0 ? (
              <span className="font-mono text-sm text-ink-100">{totalWatts.toFixed(0)} W</span>
            ) : undefined
          }
        >
          <div className="grid gap-6 md:grid-cols-3">
            {t.thermal?.length ? (
              <div>
                <div className="eyebrow mb-2">Temperature</div>
                <ul className="space-y-1.5 text-sm">
                  {t.thermal.map((sensor) => (
                    <li key={sensor.name} className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-ink-300" title={sensor.name}>
                        {sensor.name}
                      </span>
                      <Badge tone={tempTone(sensor)}>{sensor.temp_c.toFixed(0)}°C</Badge>
                    </li>
                  ))}
                </ul>
                {hottest && (
                  <div className="mt-2 text-xs text-ink-400">
                    hottest {hottest.name} at {hottest.temp_c.toFixed(0)}°C
                    {hottest.crit_c ? ` (critical ${hottest.crit_c.toFixed(0)}°C)` : ""}
                  </div>
                )}
              </div>
            ) : null}

            {t.fans?.length ? (
              <div>
                <div className="eyebrow mb-2">Fans</div>
                <ul className="space-y-1.5 text-sm">
                  {t.fans.map((fan) => (
                    <li key={fan.name} className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-ink-300" title={fan.name}>
                        {fan.name}
                      </span>
                      <span className="font-mono text-ink-100">{fan.rpm.toLocaleString()} rpm</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {t.power?.length ? (
              <div>
                <div className="eyebrow mb-2">Power</div>
                <ul className="space-y-1.5 text-sm">
                  {t.power.map((rail) => (
                    <li key={rail.name} className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-ink-300" title={rail.name}>
                        {rail.name}
                      </span>
                      <span className="font-mono text-ink-100">{rail.watts.toFixed(0)} W</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </Card>
      )}

      {t.network?.length ? (
        <Card title="Network">
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="text-left">Interface</th>
                  <th className="text-left">Link</th>
                  <th className="text-right">In</th>
                  <th className="text-right">Out</th>
                  <th className="text-right">Errors</th>
                  <th className="text-right">Dropped</th>
                </tr>
              </thead>
              <tbody>
                {t.network.map((nic) => {
                  const errors = nic.rx_errors + nic.tx_errors;
                  const dropped = nic.rx_dropped + nic.tx_dropped;
                  return (
                    <tr key={nic.name}>
                      <td className="font-mono">{nic.name}</td>
                      <td>
                        {nic.carrier ? (
                          <StatusDot
                            tone="ok"
                            label={nic.speed_mbps ? `${nic.speed_mbps} Mbps ${nic.duplex ?? ""}`.trim() : "up"}
                          />
                        ) : (
                          <StatusDot tone="idle" label="down" />
                        )}
                      </td>
                      <td className="text-right font-mono">{nic.rx_mbps.toFixed(1)}</td>
                      <td className="text-right font-mono">{nic.tx_mbps.toFixed(1)}</td>
                      <td className={`text-right font-mono ${errors ? "text-state-warn" : "text-ink-400"}`}>
                        {errors.toLocaleString()}
                      </td>
                      <td className={`text-right font-mono ${dropped ? "text-ink-300" : "text-ink-400"}`}>
                        {dropped.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-ink-400">
            Throughput in Mbps, measured across the last poll interval. Dropped counters are
            cumulative since boot and are normal in small numbers; errors are not.
          </p>
        </Card>
      ) : null}

      {(t.filesystems?.length || t.disks?.length) && (
        <Card title="Storage">
          <div className="grid gap-6 lg:grid-cols-2">
            {t.filesystems?.length ? (
              <div className="space-y-4">
                <div className="eyebrow">Filesystems</div>
                {t.filesystems.map((fs) => (
                  <Meter
                    key={fs.mount}
                    label={fs.mount}
                    pct={fs.pct}
                    tone={loadTone(fs.pct, DISK_WARN, DISK_CRIT)}
                    detail={`${fs.used_gb.toFixed(1)} of ${fs.total_gb.toFixed(1)} GB${
                      fs.inode_pct ? ` · ${fs.inode_pct.toFixed(0)}% inodes` : ""
                    }`}
                  />
                ))}
              </div>
            ) : null}

            {t.disks?.length ? (
              <div>
                <div className="eyebrow mb-2">Devices</div>
                <div className="overflow-x-auto">
                  <table className="table w-full">
                    <thead>
                      <tr>
                        <th className="text-left">Device</th>
                        <th className="text-right">Read</th>
                        <th className="text-right">Write</th>
                        <th className="text-right">Busy</th>
                        <th className="text-right">Temp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.disks.map((disk) => (
                        <tr key={disk.dev}>
                          <td className="font-mono">{disk.dev}</td>
                          <td className="text-right font-mono">{disk.read_mbps.toFixed(1)}</td>
                          <td className="text-right font-mono">{disk.write_mbps.toFixed(1)}</td>
                          <td className="text-right font-mono">{disk.util_pct.toFixed(0)}%</td>
                          <td className="text-right font-mono">
                            {disk.temp_c ? `${disk.temp_c.toFixed(0)}°C` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-ink-400">MB/s across the last poll interval.</p>
              </div>
            ) : null}
          </div>
        </Card>
      )}

      <Card title="Host">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm md:grid-cols-4">
          <div>
            <dt className="text-xs uppercase tracking-wider text-ink-400">Kernel</dt>
            <dd className="mt-0.5 font-mono text-xs text-ink-100">{t.kernel ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-ink-400">Clock</dt>
            <dd className="mt-0.5">
              {t.time ? (
                <StatusDot
                  tone={t.time.synced ? "ok" : "warn"}
                  label={t.time.synced ? `synced · ${t.time.offset_ms.toFixed(0)} ms` : "not synced"}
                />
              ) : (
                "—"
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-ink-400">ECC memory</dt>
            <dd className="mt-0.5">
              {t.ecc ? (
                <StatusDot
                  tone={t.ecc.uncorrected ? "crit" : t.ecc.corrected ? "warn" : "ok"}
                  label={`${t.ecc.corrected} corrected · ${t.ecc.uncorrected} uncorrected`}
                />
              ) : (
                <span className="text-ink-400">not reported</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-ink-400">CPU clock</dt>
            <dd className="mt-0.5 font-mono text-xs text-ink-100">
              {t.cpu?.freq_mhz
                ? `${(t.cpu.freq_mhz / 1000).toFixed(2)} GHz${
                    t.cpu.max_mhz ? ` of ${(t.cpu.max_mhz / 1000).toFixed(2)}` : ""
                  }`
                : "—"}
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}
